import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/*
 * EDI submissions — 837P batches, their 999/277CA/835 responses, and the
 * per-claim disposition those responses produce.
 *
 * Shapes here mirror `formatSubmission` + the detail-endpoint envelope in
 * [server/src/routes/edi.js]. The raw X12 payload is never delivered in JSON:
 * /payload is a separate text/plain download so the PHI-bearing claim batch
 * gets its own audit entry.
 */

export type SubmissionStatus =
    | 'pending_upload'
    | 'uploaded'
    | 'ack_accepted'
    | 'ack_rejected'
    | 'partial_paid'
    | 'paid'
    | 'failed';

export type ResponseType = '999' | '277CA' | '835';

export interface Submission {
    id: string;
    filename: string;
    interchangeControl: string;
    groupControl: string;
    isTest: boolean;
    claimCount: number;
    totalBilled: number;
    status: SubmissionStatus;
    uploadedAt: string | null;
    ack999At: string | null;
    ack277caAt: string | null;
    lastError: string;
    createdAt: string;
    updatedAt: string;
}

export interface SubmissionResponseRow {
    id: string;
    type: ResponseType;
    filename: string;
    receivedAt: string;
    status: string;
    parsed: Record<string, unknown> | null;
}

export interface SubmissionClaimRow {
    id: string;
    displayId: string;
    status: string;
    client: string;
    billed: number;
    paid: number;
    oaTraceNumber: string;
}

export interface SubmissionDetail extends Submission {
    responses: SubmissionResponseRow[];
    claims: SubmissionClaimRow[];
}

export interface SubmitClaimsResult {
    submissionId: string;
    filename: string;
    claimCount: number;
    totalBilled: number;
    bytes: number;
    isTest: boolean;
    transport: 'local' | 'sftp';
}

export interface AttachResponseInput {
    submissionId: string;
    type: ResponseType;
    filename?: string;
    raw: string;
}

export interface AttachResponseResult {
    id: string;
    type: ResponseType;
    status: string;
}

export function useSubmissions() {
    return useQuery({
        queryKey: ['edi', 'submissions'] as const,
        queryFn: async (): Promise<Submission[]> => {
            const res = await api.get<Submission[]>('/edi/submissions');
            return res.data;
        },
        staleTime: 30_000,
    });
}

export function useSubmission(id: string | undefined) {
    return useQuery({
        queryKey: ['edi', 'submission', id] as const,
        enabled: !!id,
        queryFn: async (): Promise<SubmissionDetail> => {
            const res = await api.get<SubmissionDetail>(`/edi/submissions/${id}`);
            return res.data;
        },
    });
}

export function useSubmitClaims() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { claimIds: string[]; isTest?: boolean }): Promise<SubmitClaimsResult> => {
            const res = await api.post<SubmitClaimsResult>('/edi/submissions', input);
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['edi', 'submissions'] });
            qc.invalidateQueries({ queryKey: ['claims'] });
        },
    });
}

export function useAttachEdiResponse() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: AttachResponseInput): Promise<AttachResponseResult> => {
            const { submissionId, ...body } = input;
            const res = await api.post<AttachResponseResult>(
                `/edi/submissions/${submissionId}/responses`,
                body,
            );
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['edi', 'submissions'] });
            qc.invalidateQueries({ queryKey: ['edi', 'submission', vars.submissionId] });
            qc.invalidateQueries({ queryKey: ['claims'] });
        },
    });
}

export function submissionPayloadUrl(id: string): string {
    return `/api/edi/submissions/${id}/payload`;
}
